// eslint-disable-next-line @typescript-eslint/no-unused-vars
import '@ionic/react/css/core.css';
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import 'yet-another-react-lightbox/styles.css';

import { Network } from '@capacitor/network';
import { IonApp, useIonToast } from '@ionic/react';
import { setupIonicReact } from '@ionic/react';
import { LoginModal } from '@lidea/auth/mobile/feature-modal';
import { MapTab } from '@lidea/search/mobile/feature-map-tab';
import {
  updateMeasureDefaults,
  uploadCropPictureOptions,
  warmPendingCropPicturesStore,
} from '@lidea/shared/data-access/analysis';
import {
  clearFailedMutationsByKey,
  clearMutationsByKey,
  isClientError,
  queryRetriesStrategies,
  scheduleClearFailedMutationsByKey,
} from '@lidea/shared/data-access/core';
import {
  applyNetworkConnectivity,
  installManualOfflineAwareOnlineManager,
  OfflineSyncRoot,
} from '@lidea/shared/feature-offline';
import { ProtectedRoute } from '@lidea/shared/feature-protected-route';
import { lideaMuiTheme } from '@lidea/shared/ui/core';
import { MobileTabsLayout } from '@lidea/shared/ui/layout';
import { setupNativeKeyboard } from '@lidea/shared/util/core';
import { MantineProvider } from '@mantine/core';
import { ThemeProvider } from '@mui/material/styles';
import { useDisclosure, useQueue } from '@mantine/hooks';
import { NotificationProps, Notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import {
  hashKey,
  MutationCache,
  onlineManager,
  QueryClient,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools/build/modern/production.js';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import dayjs from 'dayjs';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route } from 'react-router-dom';

import { ErrorBoundaryComponent } from './components/error-boundary/error-boundary';
import { MutationsNotifier } from './components/mutations-notifier/mutations-notifier';
// import ErrorBoundary from './components/error-boundary/error-boundary';
import ReloadPrompt from './components/reload-prompt/reload-prompt';
import EditPlot from './pages/edit-plot/edit-plot';
import ListTab from './pages/list-tab/list-tab';
import Measures from './pages/measures/measures';
import PlotDetails from './pages/plot-details/plot-details';
import Zoning from './pages/zoning/zoning';
import { createIDBPersister } from './utils/create-idb-persister';

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then(
    (d) => ({
      default: d.ReactQueryDevtools,
    })
  )
);

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

  // disable the Sentry SDK for development
  enabled:
    process.env.NODE_ENV !== 'test' && import.meta.env.MODE !== 'development',
});

setupIonicReact({
  mode: 'md',
  innerHTMLTemplatesEnabled: true,
});

installManualOfflineAwareOnlineManager();

void warmPendingCropPicturesStore();

const lideaGreen: [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
] = [
  '#f7fce8',
  '#eef9d0',
  '#dff3a8',
  '#cfec80',
  '#bfe558',
  '#97c416',
  '#85ac13',
  '#6f9010',
  '#59740d',
  '#43580a',
];

const persister = createIDBPersister();

// Create a client

// set a default mutation function so that paused mutations can resume after a page reload

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
      // keep in memory cache for Infinity to prevent query using default persister from being removed from indexeddb
      gcTime: Infinity,
      retry: queryRetriesStrategies.retryTimeout,
    },
    mutations: {
      gcTime: Infinity,
      networkMode: 'offlineFirst',
    },
  },
  mutationCache: new MutationCache({
    onSuccess: (_, __, context: any) => {
      if (context && context.notification) {
        // add(context.notification);
      } else {
        // add({
        //   message: 'Mutation completed successfully',
        //   color: 'green',
        // });
      }
    },
    onError: async (error: any, variables, context: any, mutation) => {
      if (!mutation?.options?.mutationKey) {
        return;
      }

      // add({
      //   message: error.message || 'Mutation failed',
      //   color: 'danger',
      // });
    },
    onSettled: (_data, error, _variables, _context, mutation) => {
      const mutationKey = mutation?.options?.mutationKey;
      if (!mutationKey) {
        return;
      }

      const keyHash = hashKey(mutationKey);

      if (keyHash === hashKey(updateMeasureDefaults.mutationKey)) {
        if (error && onlineManager.isOnline()) {
          scheduleClearFailedMutationsByKey(
            queryClient,
            updateMeasureDefaults.mutationKey
          );
        }
        return;
      }

      if (
        keyHash === hashKey(uploadCropPictureOptions.mutationKey) ||
        keyHash === hashKey(['resetAnalysis'])
      ) {
        return;
      }

      queryClient.invalidateQueries();
    },
  }),
});

queryClient.setMutationDefaults(
  updateMeasureDefaults.mutationKey,
  updateMeasureDefaults
);

queryClient.setMutationDefaults(
  uploadCropPictureOptions.mutationKey,
  uploadCropPictureOptions
);

export function App() {
  const { t, i18n } = useTranslation(['common', 'analysis'], {
    useSuspense: false,
  });
  const [isRestoring, setIsRestoring] = useState(true);
  const [isModalOpen, modalHandlers] = useDisclosure(false);
  const [present] = useIonToast();
  const { state, add, update } = useQueue<NotificationProps>({
    initialValues: [],
    limit: 1,
  });
  const [showDevtools, setShowDevtools] = useState(false);

  const handleProtection = () => {
    modalHandlers.open();
  };

  const handleLoginModalClose = () => {
    modalHandlers.close();
  };

  useEffect(() => {
    // @ts-expect-error - custom window property
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  useEffect(() => {
    if (state[0]?.message) {
      present({
        message: state[0]?.message as string,
        color: state[0]?.color,
        duration: 1500,
        onDidDismiss: () => {
          update((prev) => prev.filter((n) => n.message !== state[0]?.message));
        },
      });
    }
  }, [state]);

  useEffect(() => {
    let removeKeyboardListener: (() => void) | undefined;

    void setupNativeKeyboard().then((cleanup) => {
      removeKeyboardListener = cleanup;
    });

    return () => {
      removeKeyboardListener?.();
    };
  }, []);

  useEffect(() => {
    const setupListener = async () => {
      const listener = await Network.addListener('networkStatusChange', (status) => {
        if (status.connected) {
          // Real reconnect — do not unlock a user-chosen HL mode.
          const wentOnline = applyNetworkConnectivity(true);
          if (wentOnline) {
            add({
              message: t('common:online'),
            });
            queryClient.resumePausedMutations();
          }
        } else if (!navigator.onLine) {
          // Real disconnect (DevTools offline, lost WiFi…) — TanStack also listens to
          // window "offline"; this keeps native Capacitor in sync without blips where
          // Capacitor says disconnected but navigator.onLine is still true.
          applyNetworkConnectivity(false);
          add({
            message: t('common:offline'),
          });
        }
      });

      return listener;
    };

    const listenerPromise = setupListener();

    return () => {
      listenerPromise.then((listener) => listener.remove());
    };
  }, [add, queryClient, t]);

  useEffect(() => {
    (async () => {
      const networkStatus = await Network.getStatus();
      const connected = networkStatus.connected || navigator.onLine;
      if (applyNetworkConnectivity(connected)) {
        queryClient.resumePausedMutations();
      }
    })();
  }, [queryClient]);

  useEffect(() => {
    i18n.on('languageChanged', (lng) => {
      document.documentElement.setAttribute('lang', lng);
      dayjs.locale(lng);
    });
    document.documentElement.setAttribute('lang', i18n.language);

    return () => {
      i18n.off('languageChanged');
    };
  }, [i18n]);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: Infinity,
        dehydrateOptions: {
          shouldDehydrateMutation(mutation) {
            const mutationKey = mutation.options.mutationKey;
            if (!mutationKey) {
              return false;
            }

            const keyHash = hashKey(mutationKey);
            if (keyHash === hashKey(['resetAnalysis'])) {
              return false;
            }

            if (keyHash === hashKey(uploadCropPictureOptions.mutationKey)) {
              return (
                mutation.state.isPaused || mutation.state.status === 'success'
              );
            }

            if (keyHash === hashKey(updateMeasureDefaults.mutationKey)) {
              return (
                mutation.state.isPaused || mutation.state.status === 'success'
              );
            }

            const isPaused = mutation.state.isPaused;
            const isSuccess = mutation.state.status === 'success';
            const isError =
              mutation.state.status === 'error' &&
              !isClientError(mutation.state.error);

            return isPaused || isError || isSuccess;
          },
          // we use the experimental persister on a per-query basis
          // we can't use it on all queries because it does not work yet with optimistic updates
          shouldDehydrateQuery: (query) => {
            return query.options.persister
              ? false
              : // check for success or idle state
                // in offlineFirst mode, query is run once before getting paused or idle
                // this first fetch might fail if the network is not available so we want to prevent it from being dehydrated
                query.state.status === 'success' ||
                  query.state.fetchStatus === 'idle';
          },
        },
      }}
      onSuccess={async () => {
        setIsRestoring(false);
        clearMutationsByKey(queryClient, ['resetAnalysis']);

        const networkStatus = await Network.getStatus();
        const connected = networkStatus.connected || navigator.onLine;
        if (applyNetworkConnectivity(connected)) {
          clearFailedMutationsByKey(
            queryClient,
            updateMeasureDefaults.mutationKey
          );
          queryClient.resumePausedMutations();
        }
      }}
    >
      {/* <ReactQueryDevtools initialIsOpen /> */}
      {showDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtoolsProduction client={queryClient} />
        </Suspense>
      )}
      <MantineProvider
        theme={{
          fontFamily: 'Abel, Arial, Helvetica, sans-serif',
          headings: { fontFamily: 'Abel, Arial, Helvetica, sans-serif' },
          primaryColor: 'green',
          colors: { green: lideaGreen },
        }}
      >
        <ThemeProvider theme={lideaMuiTheme}>
        <Notifications />
        <ReloadPrompt />
        <Sentry.ErrorBoundary fallback={ErrorBoundaryComponent}>
          <OfflineSyncRoot>
          <IonApp>
          {!isRestoring ? <MutationsNotifier /> : null}

            <MobileTabsLayout>
            {/* START: routes */}
            <ProtectedRoute
              handleProtection={handleProtection}
              path="/map"
              exact
            >
              <MapTab />
            </ProtectedRoute>

            <ProtectedRoute
              component={EditPlot}
              handleProtection={handleProtection}
              path="/search/plots/:id/edit"
              exact
            ></ProtectedRoute>

            <ProtectedRoute
              component={Zoning}
              handleProtection={handleProtection}
              path="/search/plots/:id/analysis/:analysisId/Zoning"
              exact
            ></ProtectedRoute>

            <ProtectedRoute
              component={Measures}
              handleProtection={handleProtection}
              path="/search/plots/:id/analysis/:analysisId/measures"
              exact
            ></ProtectedRoute>

            <ProtectedRoute
              component={PlotDetails}
              handleProtection={handleProtection}
              path="/search/plots/:id"
              exact
            ></ProtectedRoute>

            <ProtectedRoute
              handleProtection={handleProtection}
              path="/search"
              exact
            >
              <ListTab />
            </ProtectedRoute>

            <Route path="/" exact>
              <Redirect to="/map" />
            </Route>
            {/* END: routes */}
            </MobileTabsLayout>
            <LoginModal close={handleLoginModalClose} isOpen={isModalOpen} />
          </IonApp>
          </OfflineSyncRoot>
        </Sentry.ErrorBoundary>
        </ThemeProvider>
      </MantineProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
